import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Gavel, TrendingUp, BellRing, CalendarDays, Volume2, VolumeX, Wallet } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];
const FANS_IMG = "https://images.unsplash.com/photo-1762013315117-1c8005ad2b41?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxleGNpdGVkJTIwc3BvcnRzJTIwZmFucyUyMHN0YWRpdW18ZW58MHx8fHwxNzg4MTk0OTkzfDA&ixlib=rb-4.1.0&q=85";
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function playGavel() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const knock = (t) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(190, ctx.currentTime + t);
      osc.frequency.exponentialRampToValueAtTime(55, ctx.currentTime + t + 0.1);
      gain.gain.setValueAtTime(0.9, ctx.currentTime + t);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + 0.14);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + t);
      osc.stop(ctx.currentTime + t + 0.16);
    };
    knock(0);
    knock(0.2);
  } catch {}
}

function NotifyCard() {
  const submit = async (e) => {
    e.preventDefault();
    const f = e.target;
    try {
      const { data } = await api.post("/subscribe", { email: f.email.value, source: "auction" });
      toast.success(data.message);
      f.reset();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save that right now.");
    }
  };
  return (
    <div className="max-w-lg backdrop-blur-xl bg-white/70 saturate-150 border border-white rounded-2xl p-6 sm:p-8" data-testid="notify-card-auction">
      <div className="flex items-center gap-2 text-league-blue mb-2">
        <CalendarDays size={16} />
        <span className="text-[11px] font-bold uppercase tracking-[0.25em]">Auction night ahead of S6.0 · 19–21 Feb 2027</span>
      </div>
      <p className="text-league-ink/70 text-sm mb-5">Drop your email and we'll notify you the moment the auction goes live.</p>
      <form onSubmit={submit} className="flex gap-3">
        <input type="email" name="email" required placeholder="you@example.com" data-testid="notify-email-auction"
          className="flex-1 min-w-0 px-4 py-3 rounded-full border border-league-blue/30 bg-white text-sm text-league-ink placeholder:text-league-ink/40 focus:outline-none focus:ring-2 focus:ring-league-blue" />
        <button type="submit" data-testid="notify-submit-auction"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-league-blue text-white text-sm font-bold uppercase tracking-wider transition-colors duration-200 hover:bg-league-red">
          <BellRing size={15} /> Notify me
        </button>
      </form>
    </div>
  );
}

function PurseTracker({ teams }) {
  const tracked = teams.filter((t) => t.purse_total > 0);
  if (!tracked.length) return null;
  return (
    <div className="mt-14" data-testid="auction-purse-tracker">
      <h2 className="font-display text-2xl sm:text-3xl text-white tracking-tight mb-6 flex items-center gap-3">
        <Wallet size={22} className="text-league-red" /> PURSE TRACKER
      </h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {tracked.map((t) => {
          const pct = t.purse_total ? Math.min(100, Math.round((t.spent / t.purse_total) * 100)) : 0;
          return (
            <div key={t.team} className="backdrop-blur-xl bg-white/10 border border-white/15 rounded-xl p-4" data-testid={`purse-${t.team.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <p className="text-xs font-bold uppercase tracking-widest text-white/60 truncate">{t.team}</p>
              <p className="font-oswald text-2xl font-semibold text-white mt-1.5">{inr(t.purse_left)} <span className="text-white/40 text-sm">left</span></p>
              <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
                <motion.div
                  className="h-full bg-league-red"
                  initial={{ width: 0 }}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.8, ease: EASE }}
                />
              </div>
              <p className="text-[10px] text-white/40 mt-1.5">{pct}% of {inr(t.purse_total)} spent</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SoldLedger({ players }) {
  const sold = players.filter((p) => p.status === "sold");
  if (!sold.length) return null;
  return (
    <div className="mt-16" data-testid="auction-sold-ledger">
      <h2 className="font-display text-3xl sm:text-4xl text-white tracking-tight mb-8">SOLD <span className="text-outline-white">LEDGER</span></h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {sold.map((p, i) => (
          <motion.div
            key={p.id}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.5, delay: (i % 3) * 0.07, ease: EASE }}
            className="backdrop-blur-xl bg-white/10 border border-white/15 rounded-xl p-5 flex items-center gap-4"
            data-testid={`auction-sold-${p.id}`}
          >
            <img src={p.image_url || "/asl-logo.png"} alt={p.name} className="w-14 h-14 rounded-full object-cover border-2 border-white/20 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-oswald text-lg font-semibold text-white truncate">{p.name}</p>
              <p className="text-xs text-white/50 uppercase tracking-widest">{p.role}</p>
              <div className="mt-1.5 flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-league-red truncate">{p.sold_to}</span>
                <span className="font-oswald text-xl font-semibold text-white shrink-0">{inr(p.sold_price)}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export default function Auction() {
  const [state, setState] = useState(null);
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("asl_auction_sound") !== "off");
  const soundRef = useRef(soundOn);
  const lastEventAt = useRef(undefined);

  soundRef.current = soundOn;

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem("asl_auction_sound", next ? "on" : "off");
    if (next) playGavel();
  };

  const fetchAll = () =>
    Promise.all([api.get("/auction/state"), api.get("/auction/players"), api.get("/auction/teams")])
      .then(([s, p, t]) => {
        const ev = s.data.last_event;
        if (ev && ev.type === "sold" && lastEventAt.current !== undefined && ev.at !== lastEventAt.current && soundRef.current) {
          playGavel();
        }
        lastEventAt.current = ev ? ev.at : null;
        setState(s.data);
        setPlayers(p.data);
        setTeams(t.data);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 4000);
    return () => clearInterval(t);
  }, []);

  const live = state?.live;
  const cur = state?.current_player;
  const last = state?.last_event;

  return (
    <main className="relative min-h-screen overflow-hidden bg-league-deep" data-testid="auction-page">
      <img src={FANS_IMG} alt="Stadium crowd" className="absolute inset-0 w-full h-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-b from-league-deep/80 via-league-blue/40 to-league-deep" />

      <button
        onClick={toggleSound}
        data-testid="auction-sound-toggle"
        className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-white text-league-blue shadow-2xl flex items-center justify-center transition-colors duration-200 hover:bg-league-red hover:text-white"
        aria-label={soundOn ? "Mute hammer sound" : "Unmute hammer sound"}
        title={soundOn ? "Hammer sound on" : "Hammer sound off"}
      >
        {soundOn ? <Volume2 size={20} /> : <VolumeX size={20} />}
      </button>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-36 pb-24 w-full">
        {live ? (
          <div data-testid="auction-live-board">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }} className="flex items-center gap-4 mb-10">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-league-red text-white text-[11px] font-bold uppercase tracking-[0.25em]" data-testid="auction-live-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Auction Live
              </span>
              <span className="text-white/60 text-xs font-semibold">Updates every 4 seconds</span>
            </motion.div>

            <AnimatePresence>
              {last && !cur && (
                <motion.div
                  key={last.at}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className={`mb-10 inline-block px-8 py-5 rounded-2xl border ${last.type === "sold" ? "bg-green-500/15 border-green-400/40" : "bg-white/10 border-white/20"}`}
                  data-testid="auction-last-event"
                >
                  {last.type === "sold" ? (
                    <p className="font-display text-3xl sm:text-4xl text-white tracking-tight">
                      SOLD! <span className="text-green-400">{last.player}</span> → {last.team} <span className="text-white/50">for</span> {inr(last.amount)}
                    </p>
                  ) : (
                    <p className="font-display text-3xl sm:text-4xl text-white/80 tracking-tight">{last.player} GOES UNSOLD</p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {cur ? (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <motion.div key={cur.id} initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: EASE }} className="lg:col-span-7" data-testid="auction-current-player">
                  <motion.img
                    src={cur.image_url || "/asl-logo.png"}
                    alt={cur.name}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.1, ease: EASE }}
                    className={`h-28 w-28 sm:h-36 sm:w-36 rounded-full object-cover border-4 border-white/25 shadow-2xl mb-6 ${cur.image_url ? "" : "bg-white p-2"}`}
                    data-testid="auction-player-photo"
                  />
                  <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-league-red flex items-center gap-2 mb-4">
                    <Gavel size={14} /> On the block
                  </p>
                  <h1 className="font-display text-white text-6xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.95]" data-testid="auction-player-name">{cur.name}</h1>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <span className="px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-white text-xs font-bold uppercase tracking-widest">{cur.role}</span>
                    <span className="px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-white text-xs font-bold uppercase tracking-widest">Base {inr(cur.base_price)}</span>
                  </div>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.12, ease: EASE }} className="lg:col-span-5 backdrop-blur-xl bg-white/10 border border-white/15 rounded-2xl p-8" data-testid="auction-bid-panel">
                  <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-white/50 flex items-center gap-2">
                    <TrendingUp size={14} /> Current bid
                  </p>
                  <motion.p
                    key={state.current_bid + (state.current_bidder || "")}
                    initial={{ scale: 1.08, color: "#ff3b30" }}
                    animate={{ scale: 1, color: "#ffffff" }}
                    transition={{ duration: 0.45 }}
                    className="font-oswald text-6xl sm:text-7xl font-semibold mt-3 leading-none"
                    data-testid="auction-current-bid"
                  >
                    {inr(state.current_bid)}
                  </motion.p>
                  <p className="mt-3 text-white/80 font-oswald font-semibold text-lg" data-testid="auction-leading-team">
                    {state.current_bidder ? `Leading — ${state.current_bidder}` : "Waiting for the first paddle…"}
                  </p>

                  {(state.log || []).length > 0 && (
                    <div className="mt-6 border-t border-white/10 pt-4" data-testid="auction-bid-log">
                      <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/40 mb-2">Bid log</p>
                      <ul className="space-y-1.5">
                        {[...state.log].reverse().slice(0, 6).map((l, i) => (
                          <li key={i} className={`flex justify-between text-sm ${i === 0 ? "text-white font-semibold" : "text-white/50"}`}>
                            <span>{l.team}</span>
                            <span className="font-oswald">{inr(l.amount)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </motion.div>
              </div>
            ) : (
              !last && (
                <p className="text-white/70 text-lg" data-testid="auction-waiting">The auctioneer is lining up the next player — paddles ready.</p>
              )
            )}

            <PurseTracker teams={teams} />
            <SoldLedger players={players} />
          </div>
        ) : (
          <div data-testid="auction-idle">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} className="flex items-center gap-3 mb-8">
              <span className="inline-flex w-12 h-12 rounded-full bg-league-red text-white items-center justify-center">
                <Gavel size={22} />
              </span>
              <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-white/80">Live Auction</span>
            </motion.div>

            <h1 className="font-display text-white leading-[0.9] tracking-tighter" data-testid="auction-idle-headline">
              <span className="block overflow-hidden">
                <motion.span className="block text-6xl sm:text-7xl lg:text-[8rem]" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 0.9, delay: 0.2, ease: EASE }}>
                  THE HAMMER
                </motion.span>
              </span>
              <span className="block overflow-hidden">
                <motion.span className="block text-6xl sm:text-7xl lg:text-[8rem] text-outline-white" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 0.9, delay: 0.35, ease: EASE }}>
                  DROPS SOON.
                </motion.span>
              </span>
            </h1>

            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.6 }} className="mt-8 max-w-xl text-white/80 text-base md:text-lg leading-relaxed">
              180+ players. Seven franchises. One war room. The ASL 6.0 mega auction plays out live on this page — paddle battles, surprise picks and all.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.75 }} className="mt-10">
              <NotifyCard />
            </motion.div>

            <SoldLedger players={players} />
          </div>
        )}
      </div>
    </main>
  );
}
